import React, { useState, useEffect } from 'react';
import { CheckboxGroup } from '@lightbeamhealth/blueprint-form';
import Button from '../../molecules/button';
import {
  FormatContact,
  GetContacts,
  GetContact,
  GetContactParams,
  GetContactsParams,
  GetContactsResponse,
  transformContact,
  transformContacts,
  IContact
} from '../../../libs/contact';
import {
  GetTag,
  GetTags,
  GetTagParams,
  GetTagsParams,
  GetTagsResponse,
  transformTag,
  transformTags,
  ITag
} from '../../../libs/tag';
import { useRequestOnEvent } from '../../../libs/hooks/useRequestJSON';
import SelectContactsDialog from '../dialog/contacts/select';
import SelectTagsDialog from '../dialog/tags/select';
import { pairEntitiesWithIds, areTags } from './utils';

type ContactRequestParams = GetContactParams | GetContactsParams;
type TagRequestParams = GetTagParams | GetTagsParams;

/**
 * Tags & Contacts filters share this Filter component
 * The component-specific options are housed in this config
 */
const FILTERS_CONFIG = {
  contacts: {
    label: 'Contacts',
    formKey: 'filters.contactIds',
    SelectDialog: SelectContactsDialog,
    formatLabel(contact: IContact) {
      return FormatContact(contact);
    },
    requestOnEvent() {
      return useRequestOnEvent<
        ContactRequestParams,
        Omit<GetContactsResponse, 'status'>
      >();
    },
    useEffectFn(groupLoading: boolean, selectedIds: number[], run: any) {
      return () => {
        if (groupLoading) {
          return;
        }
        let requestFn = () =>
          GetContacts({
            page: 0,
            limit: 5
          });
        let transformFn: any = transformContacts;
        if (selectedIds && selectedIds.length) {
          requestFn = () =>
            GetContact({
              ids: selectedIds
            });
          transformFn = transformContact;
        }
        run(requestFn, transformFn);
      };
    }
  },
  tags: {
    label: 'Tags',
    formKey: 'filters.tagIds',
    SelectDialog: SelectTagsDialog,
    formatLabel(tag: ITag) {
      return tag.value;
    },
    requestOnEvent() {
      return useRequestOnEvent<
        TagRequestParams,
        Omit<GetTagsResponse, 'status'>
      >();
    },
    useEffectFn(groupLoading: boolean, selectedIds: number[], run: any) {
      return () => {
        if (groupLoading) {
          return;
        }
        let requestFn = () =>
          GetTags({
            page: 0,
            limit: 5
          });
        let transformFn: any = transformTags;
        if (selectedIds && selectedIds.length) {
          requestFn = () =>
            GetTag({
              ids: selectedIds
            });
          transformFn = transformTag;
        }
        run(requestFn, transformFn);
      };
    }
  }
};

interface FilterProps {
  filterType: 'contacts' | 'tags';
  groupLoading: boolean;
  update: any;
  selectedIds: number[];
}

const Filter = ({
  filterType,
  groupLoading,
  update,
  selectedIds
}: FilterProps): JSX.Element => {
  const config = FILTERS_CONFIG[filterType];
  const selectedIdsAsString =
    selectedIds && selectedIds.length ? selectedIds.join() : '';
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { data, run } = config.requestOnEvent();
  const entities = data ? data[filterType] : [];
  const { selectedEntities, options } = pairEntitiesWithIds(
    selectedIds,
    entities,
    config.formatLabel
  );

  // Using a type guard here makes TS happy
  let selected: any;
  if (areTags(selectedEntities)) {
    selected = selectedEntities;
  } else {
    selected = selectedEntities;
  }

  const toggleDialog = () => {
    setIsDialogOpen(!isDialogOpen);
  };

  useEffect(config.useEffectFn(groupLoading, selectedIds, run), [
    groupLoading,
    selectedIdsAsString
  ]);

  return (
    <React.Fragment>
      <CheckboxGroup
        label={config.label}
        name={config.formKey}
        options={options}
      />
      <Button variant="minimal" className="underline" onClick={toggleDialog}>
        + Show All {config.label}
      </Button>
      <config.SelectDialog
        selected={selected}
        updateSelected={update}
        isDialogOpen={isDialogOpen}
        toggleDialog={toggleDialog}
      />
    </React.Fragment>
  );
};

export default Filter;
